import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, AlertCircle, ArrowLeft, Save, X } from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_view_category,
  save_update_addon,
  get_addon_data_res,
  delete_menu_data,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

// Food type options
const FOOD_TYPES = [
  "Veg",
  "Non-Veg",
  "Egg",
  "Egg-Free",
  "Vegan",
  "Dairy-Free",
  "Sugar-Free",
  "Gluten-Free",
  "Halal",
  "Low-Carb",
  "Spicy",
];

export default function AddEditAddon() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const primaryId = location.state?.primary_id;

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Form data
  const [addonName, setAddonName] = useState("");
  const [addonMin, setAddonMin] = useState("");
  const [addonMax, setAddonMax] = useState("");
  const [itemPricePairs, setItemPricePairs] = useState([
    { item: "", price: "", type: "Veg", active_status: 1 },
  ]);

  // Error states
  const [errors, setErrors] = useState({});
  const [itemErrors, setItemErrors] = useState([]);

  // Dialog states
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");

  // Validation helpers
  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearItemError = useCallback((index, field) => {
    setItemErrors((prev) => {
      const newErrors = [...prev];
      if (newErrors[index]) {
        delete newErrors[index][field];
      }
      return newErrors;
    });
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_view_category, fd);

      if (!response.data.error) {
        const categoryNames = response.data.message
          .filter((item) => item.show_hide === 1)
          .map((item) => ({
            value: item.primary_id,
            label: item.item_category,
          }));

        setCategoryOptions(categoryNames);

        if (primaryId) {
          fetchAddonData(categoryNames);
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [primaryId, toast]);

  // Fetch addon data for edit
  const fetchAddonData = useCallback(
    async (categories) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("addon_id", primaryId);
      fd.append("check_flag", 1);

      try {
        const response = await server_post_data(get_addon_data_res, fd);

        if (!response.data.error) {
          const data = response.data.message;
          setAddonName(data.addon_name);
          setAddonMin(data.addon_min);
          setAddonMax(data.addon_max);
          setItemPricePairs(data.items || []);

          // Match categories
          const cateNames = data.cate_name ? data.cate_name.split(",") : [];
          const matchedCategories = cateNames
            .map((name) => {
              const category = categories.find(
                (cat) => cat.label === name.trim(),
              );
              return category || null;
            })
            .filter(Boolean);

          setSelectedCategories(matchedCategories);
        }
      } catch (error) {
        console.error("Error fetching addon:", error);
        toast({
          title: "Error",
          description: "Failed to load addon data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [primaryId, toast],
  );

  // Handle category selection
  const handleCategoryChange = useCallback(
    (categoryValue) => {
      const category = categoryOptions.find(
        (cat) => cat.value === categoryValue,
      );
      if (
        category &&
        !selectedCategories.find((c) => c.value === category.value)
      ) {
        setSelectedCategories((prev) => [...prev, category]);
      }
      clearFieldError("categories");
    },
    [categoryOptions, selectedCategories, clearFieldError],
  );

  const removeCategory = useCallback((categoryValue) => {
    setSelectedCategories((prev) =>
      prev.filter((cat) => cat.value !== categoryValue),
    );
  }, []);

  // Handle item operations
  const addItemPricePair = useCallback(() => {
    setItemPricePairs((prev) => [
      ...prev,
      { item: "", price: "", type: "Veg", active_status: 1 },
    ]);
  }, []);

  const removeItemPricePair = useCallback((index) => {
    setItemPricePairs((prev) => prev.filter((_, i) => i !== index));
    setItemErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateItemField = useCallback(
    (index, field, value) => {
      setItemPricePairs((prev) => {
        const newPairs = [...prev];
        newPairs[index][field] = value;
        return newPairs;
      });
      clearItemError(index, field);
    },
    [clearItemError],
  );

  // Handle toggle status
  const handleToggleClick = useCallback((index, item) => {
    setSelectedItemIndex(index);
    setSelectedItem(item);
    setStatusChangeMessage(
      item.active_status === 1
        ? "Are you sure you want to deactivate this item?"
        : "Are you sure you want to activate this item?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedItem) return;

    setShowLoaderAdmin(true);
    const newStatus = selectedItem.active_status === 1 ? 0 : 1;

    const fd = new FormData();
    fd.append("id_for_delete", selectedItem.primary_id);
    fd.append("active_status", newStatus);
    fd.append("flag_check", 1);
    fd.append("flag_for", "4");

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update status",
          variant: "destructive",
        });
      } else {
        setItemPricePairs((prev) =>
          prev.map((item, i) =>
            i === selectedItemIndex
              ? { ...item, active_status: newStatus }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Item ${
            newStatus === 1 ? "activated" : "deactivated"
          } successfully`,
        });
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedItem(null);
      setSelectedItemIndex(null);
    }
  }, [selectedItem, selectedItemIndex, toast]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};
    const newItemErrors = [];

    // Basic validations
    if (!addonName.trim()) {
      newErrors.addonName = "Addon name is required";
    }

    if (!addonMin || addonMin === "") {
      newErrors.addonMin = "Minimum selection is required";
    }

    if (!addonMax || addonMax === "") {
      newErrors.addonMax = "Maximum selection is required";
    }

    // if (selectedCategories.length === 0) {
    //   newErrors.categories = "At least one category must be selected";
    // }

    if (itemPricePairs.length === 0) {
      newErrors.items = "At least one addon item is required";
    } else {
      // Validate each item
      itemPricePairs.forEach((pair, index) => {
        const itemError = {};

        if (!pair.item.trim()) {
          itemError.item = "Item name is required";
        }

        if (!pair.price) {
          itemError.price = "Price is required";
        } else if (isNaN(pair.price) || parseFloat(pair.price) < 0) {
          itemError.price = "Price must be a valid number";
        }

        if (!pair.type) {
          itemError.type = "Type is required";
        }

        if (Object.keys(itemError).length > 0) {
          newItemErrors[index] = itemError;
        }
      });
    }

    // Check if max is greater than number of items
    if (addonMax && itemPricePairs.length < parseInt(addonMax)) {
      newErrors.addonMax = `Maximum (${addonMax}) cannot exceed number of items (${itemPricePairs.length})`;
    }

    setErrors(newErrors);
    setItemErrors(newItemErrors);

    return Object.keys(newErrors).length === 0 && newItemErrors.length === 0;
  }, [addonName, addonMin, addonMax, selectedCategories, itemPricePairs]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const formData = new FormData();
    formData.append("main_id", primaryId || 0);
    formData.append("addon_name", addonName.trim());
    formData.append("addon_min", addonMin);
    formData.append("addon_max", addonMax);
    formData.append(
      "cate_name",
      selectedCategories.map((cat) => cat.label).join(","),
    );
    formData.append("addon_items", JSON.stringify(itemPricePairs));

    try {
      const response = await server_post_data(save_update_addon, formData);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save addon",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: primaryId
            ? "Addon updated successfully"
            : "Addon created successfully",
        });
        navigate("/item-addons");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    primaryId,
    addonName,
    addonMin,
    addonMax,
    selectedCategories,
    itemPricePairs,
    toast,
    navigate,
  ]);

  // Initial load
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <MainLayout>
      <Header
        title={primaryId ? "Edit Addon" : "Add New Addon"}
        subtitle={
          primaryId
            ? "Update addon details and items"
            : "Create a new addon group for menu items"
        }
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/item-addons")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Addons
        </Button>

        {/* Basic Information Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Addon Name */}
            <div className="space-y-2">
              <Label htmlFor="addonName">
                Addon Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="addonName"
                placeholder="e.g., Extra Toppings, Size Options"
                value={addonName}
                onChange={(e) => {
                  setAddonName(e.target.value);
                  clearFieldError("addonName");
                }}
                className={errors.addonName ? "border-destructive" : ""}
              />
              {errors.addonName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.addonName}
                </p>
              )}
            </div>

            {/* Categories Multi-Select */}
            <div className="space-y-2">
              <Label htmlFor="categories">Menu Categories</Label>
              <div className="space-y-2">
                <Select onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Selected Categories */}
                {selectedCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
                    {selectedCategories.map((cat) => (
                      <Badge
                        key={cat.value}
                        variant="secondary"
                        className="gap-2"
                      >
                        {cat.label}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-destructive"
                          onClick={() => removeCategory(cat.value)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {errors.categories && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.categories}
                </p>
              )}
            </div>

            {/* Addon Min */}
            <div className="space-y-2">
              <Label htmlFor="addonMin">
                Minimum Selection <span className="text-destructive">*</span>
              </Label>
              <Input
                id="addonMin"
                type="number"
                min="0"
                placeholder="Min addons allowed"
                value={addonMin}
                onChange={(e) => {
                  setAddonMin(e.target.value);
                  clearFieldError("addonMin");
                }}
                className={errors.addonMin ? "border-destructive" : ""}
              />
              {errors.addonMin && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.addonMin}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Minimum number of items customer must select
              </p>
            </div>

            {/* Addon Max */}
            <div className="space-y-2">
              <Label htmlFor="addonMax">
                Maximum Selection <span className="text-destructive">*</span>
              </Label>
              <Input
                id="addonMax"
                type="number"
                min="1"
                placeholder="Max addons allowed"
                value={addonMax}
                onChange={(e) => {
                  setAddonMax(e.target.value);
                  clearFieldError("addonMax");
                }}
                className={errors.addonMax ? "border-destructive" : ""}
              />
              {errors.addonMax && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.addonMax}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Maximum number of items customer can select
              </p>
            </div>
          </div>
        </div>

        {/* Addon Items Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Addon Items
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add items that customers can choose from
              </p>
            </div>
            <Button
              onClick={addItemPricePair}
              variant="outline"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </Button>
          </div>

          {errors.items && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.items}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {itemPricePairs.map((pair, index) => (
              <div
                key={index}
                className="p-4 border rounded-lg bg-muted/30 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm text-foreground">
                    Item #{index + 1}
                  </h4>
                  <div className="flex items-center gap-2">
                    {/* Toggle for Edit Mode */}
                    {primaryId && pair.primary_id && (
                      <button
                        onClick={() => handleToggleClick(index, pair)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                          pair.active_status === 1
                            ? "bg-success"
                            : "bg-muted-foreground/30",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            pair.active_status === 1
                              ? "translate-x-6"
                              : "translate-x-1",
                          )}
                        />
                      </button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItemPricePair(index)}
                      className="hover:bg-destructive/10 hover:text-destructive"
                      disabled={itemPricePairs.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Item Name */}
                  <div className="space-y-2">
                    <Label>
                      Item Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="e.g., Extra Cheese"
                      value={pair.item}
                      onChange={(e) =>
                        updateItemField(index, "item", e.target.value)
                      }
                      className={
                        itemErrors[index]?.item ? "border-destructive" : ""
                      }
                    />
                    {itemErrors[index]?.item && (
                      <p className="text-xs text-destructive">
                        {itemErrors[index].item}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="space-y-2">
                    <Label>
                      Price <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={pair.price}
                      onChange={(e) =>
                        updateItemField(index, "price", e.target.value)
                      }
                      className={
                        itemErrors[index]?.price ? "border-destructive" : ""
                      }
                    />
                    {itemErrors[index]?.price && (
                      <p className="text-xs text-destructive">
                        {itemErrors[index].price}
                      </p>
                    )}
                  </div>

                  {/* Type */}
                  <div className="space-y-2">
                    <Label>
                      Type <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={pair.type}
                      onValueChange={(value) =>
                        updateItemField(index, "type", value)
                      }
                    >
                      <SelectTrigger
                        className={
                          itemErrors[index]?.type ? "border-destructive" : ""
                        }
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FOOD_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {itemErrors[index]?.type && (
                      <p className="text-xs text-destructive">
                        {itemErrors[index].type}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/addons")}
            disabled={showLoaderAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={showLoaderAdmin}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            {showLoaderAdmin ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4" />
                {primaryId ? "Update Addon" : "Create Addon"}
              </>
            )}
          </Button>
        </div>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Item Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
